import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { RemoveAttachmentsService } from './remove-attachments.service';

@Component({
    selector: 'gtw-remove-attachments',
    templateUrl: './remove-attachments.component.html',
    styleUrls: ['./remove-attachments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RemoveAttachmentsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  showModal = true;
  entities: any[] = [];
  rowData: any = null;
  gridData: any = null;
  filterParams: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};

  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to delete the documents for all selected batch?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private removeAttachmentsService: RemoveAttachmentsService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.initializeEntities();
    this.cdr.detectChanges();
  }

  onConfirm(): void {
    this.deleteGridAttachment();
  }

  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || generalConfigFromGlobal(this.gtwGlobal);
    this.USER_SETTINGS = header.userSettingsData || this.gtwApp.USER_SETTINGS || {};
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.gridData = gridPayloadFromActiveScreen(activeScreen);
    this.filterParams = normalizeFilterParams(activeScreen);
  }

  private initializeEntities(): void {
    if (Array.isArray(this.gridData)) {
      this.entities = [...this.gridData];
      return;
    }

    const selectedRow = this.gridData?.rowData || this.rowData;
    if (selectedRow) {
      this.entities = [{
        row_id: selectedRow.object_id,
        data: selectedRow,
      }];
      return;
    }

    this.entities = [];
  }

  private buildTransJson(): any[] {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const docHeaderKey = activeScreen?.data?.settings?.doc_header_key;

    const transJson: any[] = [];
    for (const index in this.entities as any) {
      if (index === 'selected') {
        continue;
      }
      const entity = (this.entities as any)[index] || {};
      const data = entity.data || {};
      transJson.push({
        parent_key: entity.row_id,
        parent_type: activeScreen?.label,
        doc_batch_id: data.DOC_BATCH_ID,
        combination_keys: data.COMBINATION_KEY,
        screen_name: activeScreen?.label,
        object_id: entity.row_id,
        combination_key: data.COMBINATION_KEY,
        header_key: docHeaderKey ? data[docHeaderKey] : undefined,
        doc_keys: data.DOC_KEYS,
      });
    }

    return transJson.filter((item) => item.doc_batch_id != null);
  }

  private deleteGridAttachment(): void {
    const transJson = this.buildTransJson();
    if (transJson.length === 0) {
      this.gtwSnackbarService.openSnackBar('There is no documents on selected batch', 'gtw-snackbar--yellow');
      this.onCancel();
      return;
    }

    const systemAccessLevel = Number(this.USER_SETTINGS?.user?.system_access_level ?? 0);
    if (systemAccessLevel < 3) {
      this.gtwSnackbarService.openSnackBar('Please contact to System Admin', 'gtw-snackbar--yellow');
      this.onCancel();
      return;
    }

    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const objectContext = {
      trans_json: JSON.stringify(transJson),
      grid_key: activeScreen?.data?.settings?.grid_key,
    };

    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.detectChanges();

    const baseUrl = this.GENERAL_CONFIG?.base_url || '';
    this.removeAttachmentsService.deleteBatchDocuments(baseUrl, objectContext).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        const payload = response?.data || response || {};
        if (String(payload.callSuccess) === '1') {
          this.gtwSnackbarService.openSnackBar('Documents are deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(payload.errorMessage || 'Unable to delete documents.', 'gtw-snackbar--red');
          this.requestGridUpdate.emit('cancel');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting documents.', 'gtw-snackbar--red');
        this.requestGridUpdate.emit('cancel');
        this.cdr.detectChanges();
      }
    );
  }

}
